<script setup>
import 'inert-polyfill';
import { inject, ref, computed, nextTick } from 'vue';
const config = inject('config');
const gaPush = inject('gaPush');

import { useProductsStore } from '@/app/stores/productsStore.js';
const productsStore = useProductsStore();
const props = defineProps(['selectPosition', 'filter']);

const activeFocusOption = ref(null);
const btnRef = ref(null);
const allOptionsRef = ref(null);

const optionsKey = computed(() =>
  props.filter.options ? 'options' : `${productsStore.system}Options`,
);

const options = computed(() => props.filter[optionsKey.value]);

const selectedOption = computed(() => {
  const selected = options.value.filter((option) => option.selected);
  return selected[0];
});

const dropdownButtonText = computed(() => selectedOption.value?.label || props.filter.name);
const isThisDropdownOpen = computed(() => productsStore.openedDropdown === props.selectPosition);
const disabled = computed(() => props.filter.hidden || props.filter.selected);

function toggleDropdown() {
  if (!disabled.value) {
    productsStore.openedDropdown = isThisDropdownOpen.value ? null : props.selectPosition;
  }
}

function handleOptionClick(optionPosition) {
  const isDisabled =
    productsStore.dynamicFiltersArr[props.selectPosition][optionsKey.value][optionPosition][
      'disabled'
    ];

  if (!isDisabled) {
    productsStore.dynamicFiltersArr[props.selectPosition][optionsKey.value].map(
      (option) => (option.selected = false),
    );
    productsStore.dynamicFiltersArr[props.selectPosition]['selected'] = true;
    productsStore.dynamicFiltersArr[props.selectPosition][optionsKey.value][optionPosition][
      'selected'
    ] = true;
    btnRef.value.focus();
    productsStore.filterEverything();
    handleGaClick();
  }
  toggleDropdown();
}

function handleArrowOnOption(i, param) {
  activeFocusOption.value = i;

  const length = [...allOptionsRef.value].length - 1;
  const nextEl = i + param;

  if (nextEl <= length && nextEl >= 0) {
    activeFocusOption.value = nextEl;
  } else if (nextEl > length) {
    activeFocusOption.value = 0;
  } else if (nextEl < 0) {
    activeFocusOption.value = length;
  }

  [...allOptionsRef.value][activeFocusOption.value].focus();
}

async function handleArrowOnBtn() {
  productsStore.openedDropdown = props.selectPosition;

  await nextTick(() => {
    const selectedEl = [...allOptionsRef.value].filter(
      (option) => option.dataset.selected === 'true',
    );
    const whatToFocus = selectedEl.length ? [...selectedEl][0] : [...allOptionsRef.value][0];
    whatToFocus.focus();
  });
}

function closePreviousDropdown() {
  productsStore.openedDropdown = null;
}

function handleGaClick() {
  const gaPayload = {
    event: 'prod_spec_select_filter',
    filter_name: props.filter.name,
    filter_value: selectedOption.value.label,
    product_category: config.category,
    measurement_system: productsStore.system || null,
  };
  gaPush(gaPayload);
}
</script>

<template>
  <div
    class="c-product-listing-filter__select-container"
    :class="{
      'c-product-listing-filter__select-container--disabled': disabled,
    }"
    :inert="disabled"
  >
    <label
      class="c-product-listing-filter__select-label sr-only"
      :for="`select-listbox-${filter.id}`"
      >{{ filter.name }}</label
    >
    <div class="c-product-listing-filter__select">
      <button
        class="c-product-listing-filter__select-btn"
        aria-haspopup="listbox"
        :aria-owns="`select-listbox-${filter.id}`"
        :aria-labelledby="`select-listbox-${filter.id}`"
        :aria-expanded="isThisDropdownOpen"
        @click="toggleDropdown"
        @keydown.down.prevent="handleArrowOnBtn"
        @keydown.up.prevent="handleArrowOnBtn"
        @keydown.esc.prevent="toggleDropdown"
        @keydown.tab="closePreviousDropdown"
        ref="btnRef"
      >
        {{ dropdownButtonText }}
      </button>
      <div class="c-product-listing-filter__select-listbox-container">
        <ul
          class="c-product-listing-filter__select-listbox"
          role="listbox"
          :id="`select-listbox-${filter.id}`"
          tabindex="-1"
          :hidden="!isThisDropdownOpen"
        >
          <li
            v-for="(option, i) in options"
            :key="`${filter.id}-option-${i}`"
            class="c-product-listing-filter__select-option"
            @click="handleOptionClick(i)"
            :id="option.id"
            :data-value="option.value || option.valueRange"
            role="option"
            :data-disabled="
              productsStore.dynamicFiltersArr[props.selectPosition][optionsKey][i]['disabled']
            "
            :data-selected="
              productsStore.dynamicFiltersArr[props.selectPosition][optionsKey][i]['selected']
            "
            tabindex="-1"
            @keydown.tab="handleOptionClick(i)"
            @keydown.down.prevent="handleArrowOnOption(i, 1)"
            @keydown.up.prevent="handleArrowOnOption(i, -1)"
            @keydown.enter.prevent="handleOptionClick(i)"
            @keydown.esc.prevent="toggleDropdown"
            ref="allOptionsRef"
          >
            {{ option.label }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>